import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
	SplitTransaction,
	SplitTransactionSchema,
} from '@dealstack/shared/lib/common/zod/domain/transaction/transactions.zod.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { splitTransaction } from '@dealstack/frontend/app/services/Transactions.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { splitCostByPercentage } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/SplitTransactionDrawer/splitTransaction.utils.ts';

export const useSplitTransactionDrawerController = ({
	transaction,
	refreshTransactionsList,
	onClose,
}) => {
	const tCapManage = useTranslationCapManage();

	const splitTransactionsRequest = useMutation({
		mutationFn: splitTransaction,
	});

	useEffect(() => {
		if (transaction) {
			const [cost1, cost2] = splitCostByPercentage(Number(transaction.cost), 50);
			splitTransactionForm.reset({
				id: transaction.id,
				units1: Math.ceil(transaction.units / 2),
				cost1,
				units2: transaction.units - Math.ceil(transaction.units / 2),
				cost2,
			});
		}
	}, [transaction]);

	const [defaultCost1, defaultCost2] = splitCostByPercentage(Number(transaction.cost), 50);
	const splitTransactionForm = useForm<SplitTransaction>({
		mode: 'all',
		resolver: zodResolver(SplitTransactionSchema),
		defaultValues: {
			id: transaction.id,
			units1: Math.ceil(transaction.units / 2),
			cost1: defaultCost1,
			units2: transaction.units - Math.ceil(transaction.units / 2),
			cost2: defaultCost2,
		},
		shouldFocusError: false,
	});

	const onSubmit = (formValues: SplitTransaction) => {
		splitTransactionsRequest.mutate(
			{
				id: transaction.id,
				units1: formValues.units1,
				cost1: formValues.cost1,
				units2: formValues.units2,
				cost2: formValues.cost2,
			},
			{
				onSuccess: () => {
					if (refreshTransactionsList) {
						refreshTransactionsList();
					}
					showToastV2({
						title: tCapManage('toasts.success.transaction_split_successfully'),
					});
					if (onClose) {
						onClose();
					}
				},
			}
		);
	};

	return {
		onSubmit,
		splitTransactionForm,
		splitTransactionsRequest,
	};
};
